import { resolveMediaUrl } from '@/lib/media';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ExternalLink, User, Users, MessageCircle, Phone, Mail, MapPin, Globe, Clock, Download, Share2, MoreVertical, Eye, BadgeCheck, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { getBrandForLink } from '@/data/brandIcons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const getSocialHref = (url) => {
  if (!url) return '#';
  if (/^(https?:|mailto:|tel:)/i.test(url)) return url;
  return `https://${url}`;
};

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : { r: 0, g: 0, b: 0 };
};
const getLuminance = (hex) => { const { r, g, b } = hexToRgb(hex); return (0.299 * r + 0.587 * g + 0.114 * b) / 255; };
const isLightColor = (hex) => getLuminance(hex) > 0.5;
const darkenColor = (hex, amount = 0.3) => { const { r, g, b } = hexToRgb(hex); return `rgb(${Math.round(r * (1 - amount))}, ${Math.round(g * (1 - amount))}, ${Math.round(b * (1 - amount))})`; };
const lightenColor = (hex, amount = 0.15) => { const { r, g, b } = hexToRgb(hex); return `rgb(${Math.min(255, Math.round(r + (255 - r) * amount))}, ${Math.min(255, Math.round(g + (255 - g) * amount))}, ${Math.min(255, Math.round(b + (255 - b) * amount))})`; };

/* Load fonts dynamically */
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Fredoka+One&family=Playfair+Display:wght@400;700&family=Poppins:wght@400;600;700&family=Bebas+Neue&family=Montserrat:wght@400;600;700&family=Raleway:wght@400;600;700&family=Oswald:wght@400;600;700&family=Lora:wght@400;700&family=Dancing+Script:wght@400;700&family=Pacifico&family=Archivo+Black&family=Space+Grotesk:wght@400;600;700&family=Outfit:wght@400;600;700;800&family=Plus+Jakarta+Sans:wght@400;600;700&family=Lexend:wght@400;600;700&family=Syne:wght@400;600;700;800&family=DM+Serif+Display&family=Cormorant+Garamond:wght@400;600;700&family=Instrument+Serif&family=Righteous&family=Abril+Fatface&family=Bricolage+Grotesque:wght@400;600;700&family=Libre+Baskerville:wght@400;700&family=Satoshi:wght@400;700&display=swap';
fontLink.rel = 'stylesheet';
if (!document.querySelector('link[href*="Fredoka"]')) document.head.appendChild(fontLink);

const SocialSVGs = {
  instagram: (c) => <svg className="w-6 h-6" fill={c} viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>,
  tiktok: (c) => <svg className="w-6 h-6" fill={c} viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/></svg>,
  youtube: (c) => <svg className="w-6 h-6" fill={c} viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
  facebook: (c) => <svg className="w-6 h-6" fill={c} viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
  x: (c) => <svg className="w-6 h-6" fill={c} viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
  linkedin: (c) => <svg className="w-6 h-6" fill={c} viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
  spotify: (c) => <svg className="w-6 h-6" fill={c} viewBox="0 0 24 24"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>,
  whatsapp: (c) => <svg className="w-6 h-6" fill={c} viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
  pinterest: (c) => <svg className="w-6 h-6" fill={c} viewBox="0 0 24 24"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/></svg>,
  telegram: (c) => <svg className="w-6 h-6" fill={c} viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>,
};
const getSocialIcon = (platform, color) => {
  if (platform === 'email') return <Mail className="w-6 h-6" style={{ color }} />;
  const normalizedPlatform = ['whatsapp_business', 'whatsapp_community'].includes(platform) ? 'whatsapp' : platform;
  const icon = SocialSVGs[normalizedPlatform];
  return icon ? icon(color) : <ExternalLink className="w-6 h-6" style={{ color }} />;
};

/* Card style classes */
const getCardStyle = (style, card, brd, theme) => {
  switch (style) {
    case 'glass': return { backgroundColor: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.15)' };
    case 'outlined': return { backgroundColor: 'transparent', border: `2px solid ${brd}` };
    case 'shadow': return { backgroundColor: card, border: `1px solid ${brd}`, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' };
    case 'neon': return { backgroundColor: card, border: `1px solid ${theme}40`, boxShadow: `0 0 20px ${theme}20, inset 0 0 20px ${theme}05` };
    default: return { backgroundColor: card, border: `1px solid ${brd}` };
  }
};

const getBorderRadius = (shape) => {
  switch (shape) {
    case 'rounded': return '12px';
    case 'sharp': return '4px';
    default: return '35px';
  }
};

/* Animated background CSS */
const AnimatedBg = ({ type, theme }) => {
  if (type === 'none' || !type) return null;
  if (type === 'particles') return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {Array.from({ length: 25 }).map((_, i) => (
        <div key={i} className="absolute rounded-full animate-pulse"
          style={{ width: `${4 + Math.random() * 10}px`, height: `${4 + Math.random() * 10}px`, left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, backgroundColor: theme, opacity: 0.15 + Math.random() * 0.2, animationDelay: `${Math.random() * 3}s`, animationDuration: `${2 + Math.random() * 4}s` }} />
      ))}
    </div>
  );
  if (type === 'gradient-shift') return (
    <style>{`@keyframes gradShift{0%{filter:hue-rotate(0deg)}50%{filter:hue-rotate(30deg)}100%{filter:hue-rotate(0deg)}}`}</style>
  );
  if (type === 'stars') return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {Array.from({ length: 60 }).map((_, i) => (
        <div key={i} className="absolute rounded-full bg-white"
          style={{ width: `${1 + Math.random() * 2}px`, height: `${1 + Math.random() * 2}px`, left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: 0.2 + Math.random() * 0.6, animation: `starTwinkle ${1 + Math.random() * 3}s ease-in-out ${Math.random() * 5}s infinite` }} />
      ))}
      <style>{`@keyframes starTwinkle{0%,100%{opacity:0.2}50%{opacity:0.8}}`}</style>
    </div>
  );
  if (type === 'aurora') return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse 80% 50% at 20% 40%, ${theme}15, transparent), radial-gradient(ellipse 60% 40% at 70% 60%, ${theme}10, transparent)`, animation: 'auroraMove 8s ease-in-out infinite' }} />
      <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse 70% 60% at 50% 30%, rgba(6,182,212,0.08), transparent)`, animation: 'auroraMove 12s ease-in-out infinite reverse' }} />
      <style>{`@keyframes auroraMove{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-30px) scale(1.05)}}`}</style>
    </div>
  );
  if (type === 'matrix') return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {Array.from({ length: 15 }).map((_, i) => (
        <div key={i} className="absolute text-xs" style={{ left: `${5 + Math.random() * 90}%`, top: '-20px', color: theme, opacity: 0.15, animation: `matrixFall ${3 + Math.random() * 5}s linear ${Math.random() * 3}s infinite`, fontFamily: 'monospace', fontSize: `${10 + Math.random() * 6}px` }}>
          {String.fromCharCode(0x30A0 + Math.random() * 96)}
        </div>
      ))}
      <style>{`@keyframes matrixFall{0%{transform:translateY(-20px);opacity:0.3}100%{transform:translateY(100vh);opacity:0}}`}</style>
    </div>
  );
  if (type === 'fireflies') return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="absolute rounded-full"
          style={{ width: `${6 + Math.random() * 8}px`, height: `${6 + Math.random() * 8}px`, left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, backgroundColor: theme, boxShadow: `0 0 ${10 + Math.random() * 15}px ${theme}`, opacity: 0, animation: `fireflyGlow ${4 + Math.random() * 6}s ease-in-out ${Math.random() * 5}s infinite, fireflyFloat ${8 + Math.random() * 10}s ease-in-out ${Math.random() * 3}s infinite` }} />
      ))}
      <style>{`@keyframes fireflyGlow{0%,100%{opacity:0}30%,70%{opacity:0.6}}@keyframes fireflyFloat{0%,100%{transform:translate(0,0)}25%{transform:translate(30px,-20px)}50%{transform:translate(-10px,-40px)}75%{transform:translate(-30px,-10px)}}`}</style>
    </div>
  );
  if (type === 'waves') return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 320" preserveAspectRatio="none" style={{ height: '40%', opacity: 0.06 }}>
        <path d="M0,224L48,213C96,203,192,181,288,186.7C384,192,480,224,576,224C672,224,768,192,864,181.3C960,171,1056,181,1152,186.7C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" fill={theme} style={{ animation: 'waveShift 6s ease-in-out infinite' }} />
      </svg>
      <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 320" preserveAspectRatio="none" style={{ height: '35%', opacity: 0.04 }}>
        <path d="M0,288L48,272C96,256,192,224,288,213.3C384,203,480,213,576,229.3C672,245,768,267,864,261.3C960,256,1056,224,1152,208C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" fill={theme} style={{ animation: 'waveShift 8s ease-in-out infinite reverse' }} />
      </svg>
      <style>{`@keyframes waveShift{0%,100%{transform:translateX(0)}50%{transform:translateX(-30px)}}`}</style>
    </div>
  );
  if (type === 'snow') return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {Array.from({ length: 30 }).map((_, i) => (
        <div key={i} className="absolute rounded-full bg-white"
          style={{ width: `${2 + Math.random() * 4}px`, height: `${2 + Math.random() * 4}px`, left: `${Math.random() * 100}%`, top: '-10px', opacity: 0.2 + Math.random() * 0.4, animation: `snowFall ${5 + Math.random() * 8}s linear ${Math.random() * 5}s infinite` }} />
      ))}
      <style>{`@keyframes snowFall{0%{transform:translateY(-10px) translateX(0)}50%{transform:translateY(50vh) translateX(${Math.random() > 0.5 ? '' : '-'}20px)}100%{transform:translateY(100vh) translateX(0);opacity:0}}`}</style>
    </div>
  );
  return null;
};

/* Background Pattern overlay */
const BgPattern = ({ pattern, theme }) => {
  if (!pattern || pattern === 'none') return null;
  const color = theme + '0A';
  const patterns = {
    dots: `radial-gradient(${color} 1px, transparent 1px)`,
    grid: `linear-gradient(${color} 1px, transparent 1px), linear-gradient(90deg, ${color} 1px, transparent 1px)`,
    diagonal: `repeating-linear-gradient(45deg, transparent, transparent 10px, ${color} 10px, ${color} 11px)`,
    hexagon: `radial-gradient(${color} 2px, transparent 2px)`,
    waves: `repeating-linear-gradient(0deg, transparent, transparent 20px, ${color} 20px, ${color} 21px)`,
  };
  const sizes = { dots: '20px 20px', grid: '30px 30px', diagonal: '', hexagon: '24px 24px', waves: '' };
  if (pattern === 'noise') return (
    <div className="fixed inset-0 pointer-events-none z-0" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`, opacity: 0.03 }} />
  );
  return <div className="fixed inset-0 pointer-events-none z-0" style={{ backgroundImage: patterns[pattern], backgroundSize: sizes[pattern] || 'auto', opacity: 0.4 }} />;
};

/* Countdown Timer */
const CountdownTimer = ({ date, label, txt, sub, theme }) => {
  const [timeLeft, setTimeLeft] = useState({});
  useEffect(() => {
    const calc = () => {
      const diff = new Date(date) - new Date();
      if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      return { days: Math.floor(diff / 86400000), hours: Math.floor((diff % 86400000) / 3600000), minutes: Math.floor((diff % 3600000) / 60000), seconds: Math.floor((diff % 60000) / 1000) };
    };
    setTimeLeft(calc());
    const timer = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(timer);
  }, [date]);
  return (
    <div className="text-center mb-4" data-testid="countdown-timer">
      {label && <p className="text-sm font-medium mb-2" style={{ color: theme }}>{label}</p>}
      <div className="flex items-center justify-center gap-3">
        {[['days', timeLeft.days], ['hrs', timeLeft.hours], ['min', timeLeft.minutes], ['sec', timeLeft.seconds]].map(([unit, val]) => (
          <div key={unit} className="text-center">
            <div className="text-2xl font-bold" style={{ color: txt }}>{String(val || 0).padStart(2, '0')}</div>
            <div className="text-xs uppercase" style={{ color: sub }}>{unit}</div>
          </div>
        ))}
      </div>
    </div>
  );
};


/* Product Image Carousel */
const ProductImageCarousel = ({ images, title }) => {
  const [current, setCurrent] = useState(0);
  const touchStart = useRef(0);
  const handleTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0 && current < images.length - 1) setCurrent(c => c + 1);
      if (diff < 0 && current > 0) setCurrent(c => c - 1);
    }
  };
  return (
    <div className="relative w-full h-full" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      {/* First image relative for height */}
      <img src={resolveMediaUrl(images[0])} alt={`${title} 1`}
        className="w-full h-full object-cover transition-opacity duration-300"
        style={{ opacity: current === 0 ? 1 : 0 }}
        onError={(e) => { e.target.style.display = 'none'; }} />
      {/* Overlay images */}
      {images.slice(1).map((img, idx) => (
        <img key={idx + 1} src={resolveMediaUrl(img)} alt={`${title} ${idx + 2}`}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
          style={{ opacity: idx + 1 === current ? 1 : 0 }}
          onError={(e) => { e.target.style.display = 'none'; }} />
      ))}
      {/* Image counter */}
      <div className="absolute top-2 right-2 bg-black/70 text-white text-xs font-bold px-2 py-1 rounded-full z-10">
        {current + 1}/{images.length}
      </div>
      {/* Left/Right tap zones */}
      <div className="absolute inset-y-0 left-0 w-1/3 z-10 cursor-pointer" onClick={(e) => { e.stopPropagation(); if (current > 0) setCurrent(c => c - 1); }} />
      <div className="absolute inset-y-0 right-0 w-1/3 z-10 cursor-pointer" onClick={(e) => { e.stopPropagation(); if (current < images.length - 1) setCurrent(c => c + 1); }} />
      {/* Dots */}
      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2 z-10">
        {images.map((_, idx) => (
          <button key={idx} onClick={(e) => { e.stopPropagation(); setCurrent(idx); }}
            className="w-2.5 h-2.5 rounded-full transition-all shadow-md"
            style={{ backgroundColor: idx === current ? '#fff' : 'rgba(255,255,255,0.5)', transform: idx === current ? 'scale(1.3)' : 'scale(1)' }} />
        ))}
      </div>
    </div>
  );
};

/* Video thumbnail using server-generated image */
const VideoThumb = ({ vid, vi, cardProps, onClick }) => {
  const thumbSrc = vid.thumbnail_url
    ? resolveMediaUrl(vid.thumbnail_url)
    : null;
  const videoSrc = resolveMediaUrl(vid.url);

  return (
    <div className="cursor-pointer group relative rounded-xl overflow-hidden"
      style={{ ...cardProps, aspectRatio: '9/16' }}
      data-testid={`video-thumb-${vi}`}
      onClick={onClick}>
      {thumbSrc ? (
        <img src={thumbSrc} alt={vid.title || ''} className="w-full h-full object-cover" />
      ) : (
        <video src={`${videoSrc}#t=0.1`} className="w-full h-full object-cover" muted playsInline preload="auto" />
      )}
      {/* Play overlay */}
      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center z-10">
        <div className="w-10 h-10 rounded-full bg-white/25 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
          <svg className="w-5 h-5 ml-0.5" fill="white" viewBox="0 0 24 24">
            <path d="M8 5.14v14l11-7-11-7z"/>
          </svg>
        </div>
      </div>
      {vid.title && (
        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent z-10">
          <p className="text-white text-[10px] font-medium truncate">{vid.title}</p>
        </div>
      )}
    </div>
  );
};

const EventMediaCarousel = ({ items, title, theme }) => {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef(0);

  const renderItem = (m) => {
    if (m.type === 'image') return <img src={resolveMediaUrl(m.src)} alt={title} className="w-full h-full object-cover" />;
    if (m.type === 'youtube') return <iframe src={`https://www.youtube.com/embed/${m.id}`} className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={title} />;
    if (m.type === 'vimeo') return <iframe src={`https://player.vimeo.com/video/${m.id}`} className="w-full h-full" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title={title} />;
    if (m.type === 'video') return <video src={resolveMediaUrl(m.src)} controls className="w-full h-full object-cover" />;
    return null;
  };

  return (
    <div className="relative aspect-video overflow-hidden"
      onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
      onTouchEnd={e => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (diff > 40 && current < items.length - 1) setCurrent(c => c + 1);
        if (diff < -40 && current > 0) setCurrent(c => c - 1);
      }}>
      <div className="flex transition-transform duration-300 h-full"
        style={{ width: `${items.length * 100}%`, transform: `translateX(-${current * (100 / items.length)}%)` }}>
        {items.map((m, idx) => (
          <div key={idx} className="h-full flex-shrink-0" style={{ width: `${100 / items.length}%` }}>
            {renderItem(m)}
          </div>
        ))}
      </div>
      {items.length > 1 && (
        <>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {items.map((_, idx) => (
              <button key={idx} onClick={() => setCurrent(idx)}
                className="w-2 h-2 rounded-full transition-all"
                style={{ backgroundColor: idx === current ? theme : 'rgba(255,255,255,0.4)', transform: idx === current ? 'scale(1.3)' : 'scale(1)' }} />
            ))}
          </div>
          <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-semibold"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', color: '#fff' }}>
            {current + 1} / {items.length}
          </div>
        </>
      )}
    </div>
  );
};

const PublicProfile = () => {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('links');
  const [tradeServices, setTradeServices] = useState([]);
  const [events, setEvents] = useState([]);
  const [rsvpForm, setRsvpForm] = useState({ eventId: null, name: '', email: '', phone: '' });
  const [rsvpSubmitting, setRsvpSubmitting] = useState(false);
  const [tradeRequestForm, setTradeRequestForm] = useState({ open: false, service: null, name: '', email: '', message: '', step: 0, selectedDate: '', selectedTime: '' });
  const [tradeMonth, setTradeMonth] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() });
  const [showContactForm, setShowContactForm] = useState(false);
  const [showHours, setShowHours] = useState(false);
  const [contactFormData, setContactFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingStep, setBookingStep] = useState(0);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [bookingForm, setBookingForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [bookingResult, setBookingResult] = useState(null);
  const [bookingMonth, setBookingMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });
  const [showQR, setShowQR] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const [activePoster, setActivePoster] = useState(null);
  const [publicAppreciations, setPublicAppreciations] = useState([]);
  const [refStats, setRefStats] = useState({ given: 0, received: 0 });
  const [reviewState, setReviewState] = useState({ selected: [], tone: 'casual', reviews: [], chosenIdx: 0, loading: false, step: 'select' });
  const [loadingSlots, setLoadingSlots] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/u/${username}`);
      setProfile(r.data);
      if (r.data.links?.length > 0) setActiveTab('links');
      else if (r.data.products?.length > 0) setActiveTab('shop');
      else if (r.data.about_sections?.length > 0) setActiveTab('about');
      else if (r.data.trade_services?.length > 0) setActiveTab('trade');
      else if (r.data.events?.length > 0) setActiveTab('events');
      if (r.data.trade_services) setTradeServices(r.data.trade_services);
      if (r.data.events) setEvents(r.data.events);
    }
    catch (err) {
      if (err.response?.status === 404) {
        // Check if this path is a short link or QR code
        try {
          const res = await axios.get(`${API}/resolve/${username}`);
          if (res.data?.url) { window.location.href = res.data.url; return; }
        } catch {}
        setError('Profile not found');
      } else {
        setError('Something went wrong');
      }
    }
    finally { setLoading(false); }
  }, [username]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  useEffect(() => {
    if (profile) {
      axios.get(`${API}/u/${username}/appreciations`).then(r => setPublicAppreciations(r.data)).catch(() => {});
      axios.get(`${API}/u/${username}/referral-stats`).then(r => setRefStats(r.data)).catch(() => {});
    }
  }, [profile, username]);

  const handleLinkClick = async (link) => {
    try { await axios.post(`${API}/u/${username}/click/${link.id}`); } catch (e) {}
    const url = link.url.startsWith('http') ? link.url : `https://${link.url}`;
    window.location.href = url;
  };

  const handleCTAClick = () => {
    const cta = profile?.cta_button;
    if (!cta) return;
    if (cta.action === 'call') window.location.href = `tel:${cta.value}`;
    else if (cta.action === 'email') window.location.href = `mailto:${cta.value}`;
    else if (cta.action === 'whatsapp') window.location.href = `https://wa.me/${cta.value.replace(/\D/g, '')}`;
    else {
      const url = cta.value.startsWith('http') ? cta.value : `https://${cta.value}`;
      window.location.href = url;
    }
  };

  const handleSaveContact = () => {
    const link = document.createElement('a');
    link.href = `${API}/u/${username}/vcard`;
    link.download = `${username}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Contact downloading...');
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: profile.display_name, text: `https://createonelink.com/${profile.username}`, url: `https://createonelink.com/${profile.username}` }); } catch (e) {} }
    else { navigator.clipboard.writeText(url); toast.success('Link copied!'); }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const enabledFields = (profile.contact_form_fields || []).filter(f => f.enabled);
    const required = enabledFields.filter(f => f.id === 'name' || f.id === 'email');
    for (const f of required) {
      if (!contactFormData[f.id]) { toast.error(`${f.label} is required`); return; }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/u/${username}/contact`, { fields: contactFormData });
      toast.success('Contact shared!');
      setContactFormData({});
      setShowContactForm(false);
    } catch (err) { toast.error('Failed to send'); } finally { setSubmitting(false); }
  };

  const fetchSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const res = await axios.get(`${API}/u/${username}/booking-slots?date=${date}`);
      setAvailableSlots(res.data.slots || []);
    } catch { setAvailableSlots([]); } finally { setLoadingSlots(false); }
  };

  const handleDateSelect = (dateStr) => {
    setSelectedDate(dateStr);
    setSelectedSlot('');
    fetchSlots(dateStr);
    setBookingStep(2);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!bookingForm.name || !bookingForm.email) { toast.error('Name and email are required'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/u/${username}/book`, {
        service_id: selectedService?.id, service_name: selectedService?.name || 'General',
        date: selectedDate, time_slot: selectedSlot,
        name: bookingForm.name, email: bookingForm.email, phone: bookingForm.phone, message: bookingForm.message,
      });
      setBookingResult(res.data);
      setBookingStep(4);
      toast.success('Appointment booked!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Booking failed'); } finally { setSubmitting(false); }
  };

  const getCalendarDays = () => {
    const { year, month } = bookingMonth;
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const days = [];
    for (let i = 0; i < (firstDay === 0 ? 6 : firstDay - 1); i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      const avail = profile.booking_availability?.[dayName];
      const isPast = date < today;
      const isAvailable = !isPast && avail?.enabled;
      days.push({ day: d, date: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isAvailable, isPast });
    }
    return days;
  };


  if (loading) return <div className="min-h-screen flex items-center justify-center bg-neutral-100"><div className="w-8 h-8 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /></div>;
  if (error) return <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-neutral-100"><User className="w-16 h-16 text-gray-400 mb-4" /><h1 className="font-bold text-2xl text-gray-800 mb-2">{error}</h1></div>;

  const bg = profile.background_color || '#f3f3f1';
  const isGradient = bg.includes('gradient');
  const bgStyle = isGradient ? { background: bg } : { backgroundColor: bg };
  const bgColorForCalc = isGradient ? (bg.match(/#[0-9a-fA-F]{6}/)?.[0] || '#050505') : bg;
  const theme = profile.theme_color || '#43E660';
  const light = isLightColor(bgColorForCalc);
  const txt = light ? darkenColor(bgColorForCalc, 0.75) : '#ffffff';
  const sub = light ? darkenColor(bgColorForCalc, 0.45) : 'rgba(255,255,255,0.6)';
  const card = light ? lightenColor(bgColorForCalc, 0.35) : 'rgba(255,255,255,0.08)';
  const brd = light ? darkenColor(bgColorForCalc, 0.35) : 'rgba(255,255,255,0.15)';
  const iconClr = light ? darkenColor(bgColorForCalc, 0.5) : 'rgba(255,255,255,0.8)';

  const socialLinks = profile.social_links?.filter(l => l.is_active) || [];
  const links = profile.links || [];
  const products = profile.products || [];
  const catalogTitle = profile.catalog_title || (profile.catalog_type === 'services' ? 'Services' : 'Shop');
  const socialGrid = profile.social_grid;
  const defaultOrder = ['links', 'social_icons', 'video', 'videos', 'posters', 'business_hours', 'contact_info', 'social_grid', 'testimonials', 'appreciations', 'network_stats', 'review_generator', 'exchange_contact', 'book_appointment', 'cta_button', 'save_contact'];
  const rawOrder = profile.section_order || defaultOrder;
  const hiddenSections = profile.hidden_sections || [];
  // Ensure any new sections are included even if saved order is old, filter out hidden ones
  const sectionOrder = [...rawOrder, ...defaultOrder.filter(s => !rawOrder.includes(s))].filter(s => !hiddenSections.includes(s));
  const fontFamily = profile.font_family || 'Fredoka One';
  const publicFontStyle = { fontFamily: `'${fontFamily}', sans-serif` };
  const cardSt = profile.card_style || 'solid';
  const btnShape = profile.button_shape || 'pill';
  const borderR = getBorderRadius(btnShape);
  const cardProps = getCardStyle(cardSt, card, brd, theme);
  const linkLayout = profile.link_layout || 'list';
  const profileUrl = `${window.location.origin}/${profile.username}`;

  return (
    <div className="min-h-screen relative" style={{ ...bgStyle, ...publicFontStyle, ...(profile.animated_bg === 'gradient-shift' ? { animation: 'gradShift 10s ease infinite' } : {}) }}>
      <AnimatedBg type={profile.animated_bg} theme={theme} />
      <BgPattern pattern={profile.bg_pattern} theme={theme} />

      {/* Profile link at top */}
      <div className="max-w-[680px] mx-auto px-4 pt-3 pb-1">
        <div className="flex items-center justify-center">
          <p data-testid="top-profile-link"
            className="text-sm font-semibold cursor-pointer hover:opacity-80 transition-opacity inline-block px-4 py-1.5 rounded-full"
            style={{ color: txt, backgroundColor: light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)' }}
            onClick={() => { navigator.clipboard.writeText(`https://createonelink.com/${profile.username}`); toast.success('Link copied!'); }}>
            createonelink.com/{profile.username}
          </p>
        </div>
      </div>

      {/* Hero image with gradient fade */}
      {profile.avatar_url && (
        <div className="max-w-[680px] mx-auto px-4 pt-4">
          <div className="relative overflow-hidden rounded-[1.5rem]">
            {/* Action icons overlaid on image */}
            <div className="absolute top-3 left-3 right-3 z-10 flex items-center justify-between">
              <button data-testid="share-btn" onClick={handleSaveContact} className="w-9 h-9 rounded-full flex items-center justify-center bg-white/80 backdrop-blur-sm hover:bg-white transition-all">
                <Download className="w-4 h-4 text-gray-700" />
              </button>
              <button data-testid="share-profile-btn" onClick={handleShare} className="w-9 h-9 rounded-full flex items-center justify-center bg-white/80 backdrop-blur-sm hover:bg-white transition-all">
                <Share2 className="w-4 h-4 text-gray-700" />
              </button>
            </div>
            <img
              data-testid="hero-image"
              src={resolveMediaUrl(profile.avatar_url)}
              alt={profile.display_name}
              className="w-full object-cover"
              style={{ maxHeight: '65vh', minHeight: '280px' }}
              onError={(e) => { e.target.closest('.relative').style.display = 'none'; }}
            />
            {/* Gradient fade overlay at bottom of image */}
            <div
              className="absolute bottom-0 left-0 right-0 pointer-events-none"
              style={{
                height: '40%',
                background: `linear-gradient(to top, ${bgColorForCalc} 0%, ${bgColorForCalc}CC 25%, ${bgColorForCalc}66 50%, transparent 100%)`
              }}
            />
          </div>
        </div>
      )}

      <div className="max-w-[680px] mx-auto px-5">

        {/* Brand name - retro bold with 3D shadow */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="text-center mt-4">
          <h1
            data-testid="profile-name"
            className="leading-tight inline-flex items-center justify-center gap-2"
            style={{
              fontFamily: `'${fontFamily}', cursive`,
              color: txt,
              fontSize: 'clamp(2.5rem, 10vw, 4.2rem)',
              textShadow: `3px 3px 0px ${darkenColor(bgColorForCalc, 0.7)}, 4px 4px 0px ${darkenColor(bgColorForCalc, 0.6)}, 5px 5px 0px ${darkenColor(bgColorForCalc, 0.5)}`,
              letterSpacing: '-0.01em',
            }}
          >
            {profile.display_name || `@${profile.username}`}
            {profile.show_badge && <BadgeCheck data-testid="verified-badge" className="w-8 h-8 inline-block" style={{ color: theme, filter: `drop-shadow(0 0 6px ${theme}60)` }} />}
          </h1>
          {profile.owner_name && (
            <p data-testid="owner-name" className="mt-1 text-lg font-medium" style={{ color: sub, fontFamily: `'${fontFamily}', sans-serif` }}>{profile.owner_name}</p>
          )}
          {profile.business_name && (
            <p data-testid="business-name" className="mt-1 text-lg font-medium" style={{ color: theme, fontFamily: `'${fontFamily}', sans-serif`, opacity: 0.9 }}>{profile.business_name}</p>
          )}
          {(profile.tagline || profile.bio) && (
            <p className="mt-2 text-base max-w-md mx-auto" style={{ color: sub, fontFamily: `'${fontFamily}', sans-serif` }}>{profile.tagline || profile.bio}</p>
          )}

          {/* Visitor counter */}
          {profile.visitor_count_visible && profile.visitor_count > 0 && (
            <div data-testid="visitor-counter" className="flex items-center justify-center gap-1.5 mt-2" style={{ color: sub }}>
              <Eye className="w-3.5 h-3.5" /> <span className="text-xs font-medium">{profile.visitor_count.toLocaleString()} views</span>
            </div>
          )}

          <div className="flex items-center justify-center gap-2 mt-2">
            <p data-testid="profile-bio-link"
              className="text-sm font-semibold cursor-pointer hover:opacity-80 transition-opacity inline-block px-4 py-1.5 rounded-full"
              style={{ color: txt, backgroundColor: light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)' }}
              onClick={() => { navigator.clipboard.writeText(`createonelink.com/${profile.username}`); toast.success('Link copied!'); }}>
              createonelink.com/{profile.username}
            </p>
            <button data-testid="qr-toggle" onClick={() => setShowQR(!showQR)}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
              style={{ backgroundColor: light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)' }}>
              <svg className="w-4 h-4" fill={iconClr} viewBox="0 0 24 24"><path d="M3 11h2V9H3v2zm0-4h2V3H3v4zm4 4h2V9H7v2zm0-4h2V3H7v4zM3 15h8V9H3v6zm4-4h2v2H7v-2zM13 3v8h8V3h-8zm6 6h-4V5h4v4zM3 21h8v-8H3v8zm2-6h4v4H5v-4zm8 6h2v-2h-2v2zm0-8h2v4h2v-4h2V9h-2v2h-2V9h-2v4zm4 4h2v-2h-2v2zm0 4h2v-4h-2v4zm-4-4h2v-2h-2v2z"/></svg>
            </button>
          </div>

          {/* QR Code popup */}
          {showQR && (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              className="mt-4 inline-block p-4 rounded-2xl" style={{ backgroundColor: '#fff' }}>
              <QRCodeSVG value={profileUrl} size={160} level="H" fgColor="#000" bgColor="#fff" />
              <p className="text-xs text-gray-500 mt-2">Scan to visit profile</p>
            </motion.div>
          )}

          {/* Countdown Timer */}
          {profile.countdown_date && (
            <div className="mt-4">
              <CountdownTimer date={profile.countdown_date} label={profile.countdown_label} txt={txt} sub={sub} theme={theme} />
            </div>
          )}
        </motion.div>

        {/* Tabs */}
        {(links.length > 0 || products.length > 0 || profile.about_sections?.length > 0 || tradeServices.length > 0 || events.length > 0) && (
          <div className="flex items-center justify-center mt-6 mb-6">
            <div className="inline-flex rounded-full p-1" style={{ backgroundColor: light ? darkenColor(bgColorForCalc, 0.1) : 'rgba(255,255,255,0.1)' }}>
              {links.length > 0 && (
                <button data-testid="tab-links" onClick={() => setActiveTab('links')}
                  className="px-6 py-2 rounded-full text-sm font-semibold transition-all"
                  style={{ backgroundColor: activeTab === 'links' ? (light ? '#fff' : theme) : 'transparent', color: activeTab === 'links' ? (light ? '#000' : '#fff') : sub }}>
                  Links
                </button>
              )}
              {products.length > 0 && (
                <button data-testid="tab-shop" onClick={() => setActiveTab('shop')}
                  className="px-6 py-2 rounded-full text-sm font-semibold transition-all"
                  style={{ backgroundColor: activeTab === 'shop' ? (light ? '#fff' : theme) : 'transparent', color: activeTab === 'shop' ? (light ? '#000' : '#fff') : sub }}>
                  {catalogTitle}
                </button>
              )}
              {profile.about_sections?.length > 0 && (
                <button data-testid="tab-about" onClick={() => setActiveTab('about')}
                  className="px-6 py-2 rounded-full text-sm font-semibold transition-all"
                  style={{ backgroundColor: activeTab === 'about' ? (light ? '#fff' : theme) : 'transparent', color: activeTab === 'about' ? (light ? '#000' : '#fff') : sub }}>
                  About
                </button>
              )}
              {tradeServices.length > 0 && (
                <button data-testid="tab-trade" onClick={() => setActiveTab('trade')}
                  className="px-6 py-2 rounded-full text-sm font-semibold transition-all"
                  style={{ backgroundColor: activeTab === 'trade' ? (light ? '#fff' : theme) : 'transparent', color: activeTab === 'trade' ? (light ? '#000' : '#fff') : sub }}>
                  Trade
                </button>
              )}
              {events.length > 0 && (
                <button data-testid="tab-events" onClick={() => setActiveTab('events')}
                  className="px-6 py-2 rounded-full text-sm font-semibold transition-all"
                  style={{ backgroundColor: activeTab === 'events' ? (light ? '#fff' : theme) : 'transparent', color: activeTab === 'events' ? (light ? '#000' : '#fff') : sub }}>
                  Events
                </button>
              )}
            </div>
          </div>
        )}

        {/* === LINKS TAB CONTENT === */}
        {activeTab === 'links' && (
          <>
            {sectionOrder.map((sectionId) => {
              if (sectionId === 'social_icons') return (
                socialLinks.length > 0 && (
                  <div key="social_icons" className="flex items-center justify-center gap-3 mb-4 flex-wrap">
                    {socialLinks.slice(0, 8).map((s) => (
                      <a key={s.id} href={getSocialHref(s.url)} target="_blank" rel="noopener noreferrer" data-testid={`social-${s.platform}`}
                        className="w-11 h-11 rounded-full flex items-center justify-center hover:scale-110 transition-all"
                        style={{ backgroundColor: light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.12)' }}>
                        {getSocialIcon(s.platform, iconClr)}
                      </a>
                    ))}
                  </div>
                )
              );

              if (sectionId === 'links') return (
                links.length > 0 && (
                  linkLayout === 'grid' ? (
                    <div key="links" className="mb-6 grid grid-cols-2 gap-3">
                      {links.map((link, i) => {
                        const brand = getBrandForLink(link);
                        const cardBgStyle = brand
                          ? { background: brand.gradient || brand.color }
                          : { ...cardProps };
                        const iconColor = brand ? (brand.textColor || '#fff') : txt;
                        return (
                          <motion.button key={link.id} data-testid={`link-${link.id}`}
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                            onClick={() => handleLinkClick(link)}
                            className="w-full transition-all hover:scale-[1.03] hover:brightness-110 active:scale-[0.97] flex flex-col items-center justify-center gap-1.5 p-2.5"
                            style={{ ...cardBgStyle, borderRadius: borderR, height: '100px' }}>
                            {brand ? (
                              <div className="w-8 h-8 flex items-center justify-center"
                                dangerouslySetInnerHTML={{ __html: brand.svg.replace('viewBox', `width="32" height="32" viewBox`) }} />
                            ) : (
                              link.icon && (
                                <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-lg"
                                  style={{ backgroundColor: light ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.1)', border: `2px solid ${brd}` }}>
                                  {link.icon}
                                </div>
                              )
                            )}
                            <span className="font-semibold text-xs text-center leading-tight line-clamp-2"
                              style={{ color: brand ? (brand.textColor || '#fff') : txt }}>{link.title}</span>
                          </motion.button>
                        );
                      })}
                    </div>
                  ) : (
                    <div key="links" className="mb-6" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      {links.map((link, i) => {
                        const hasThumbnail = link.thumbnail_url || (link.icon && link.icon.startsWith('http'));
                        const hasEmoji = link.icon && !link.icon.startsWith('http') && !link.thumbnail_url;
                        return (
                        <motion.button key={link.id} data-testid={`link-${link.id}`}
                          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                          onClick={() => handleLinkClick(link)}
                          className="w-full transition-all hover:scale-[1.02] hover:brightness-110 active:scale-[0.98] relative"
                          style={{ height: '70px' }}>
                          <div className="w-full h-full flex items-center pr-4"
                            style={{
                              paddingLeft: (hasThumbnail || hasEmoji) ? '60px' : '24px',
                              ...cardProps,
                              borderRadius: borderR,
                            }}>
                            <div className="flex-1 text-center min-w-0">
                              <span className="font-semibold block text-sm" style={{ color: txt }}>{link.title}</span>
                            </div>
                            <div className="flex-shrink-0 opacity-50">
                              <MoreVertical className="w-4 h-4" style={{ color: txt }} />
                            </div>
                          </div>
                          {hasThumbnail && (
                            <div className="absolute top-1/2 -translate-y-1/2 rounded-full overflow-hidden z-10"
                              style={{ left: '-8px', width: '60px', height: '60px', border: `2.5px solid ${darkenColor(bgColorForCalc, 0.4)}` }}>
                              <img src={resolveMediaUrl(link.thumbnail_url || link.icon)} alt="" className="w-full h-full object-cover"
                                onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
                            </div>
                          )}
                          {hasEmoji && (
                            <div className="absolute top-1/2 -translate-y-1/2 rounded-full overflow-hidden z-10 flex items-center justify-center text-lg"
                              style={{ left: '-8px', width: '60px', height: '60px', border: `2.5px solid ${darkenColor(bgColorForCalc, 0.4)}`, backgroundColor: lightenColor(bgColorForCalc, 0.1) }}>
                              {link.icon}
                            </div>
                          )}
                        </motion.button>
                        );
                      })}
                    </div>
                  )
                )
              );

              if (sectionId === 'contact_info') return (
                (profile.phone || profile.address || profile.website) && (
                  <div key="contact_info" className="rounded-2xl p-5 mb-4" style={{ backgroundColor: card, border: `1.5px solid ${brd}` }}>
                    <div className="space-y-3">
                      {profile.phone && <a href={`tel:${profile.phone}`} className="flex items-center gap-3 hover:opacity-70" style={{ color: txt }}><Phone className="w-5 h-5" style={{ color: iconClr }} /><span>{profile.phone}</span></a>}
                      {profile.address && <a href={`https://maps.google.com/?q=${encodeURIComponent(profile.address)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:opacity-70" style={{ color: txt }}><MapPin className="w-5 h-5" style={{ color: iconClr }} /><span>{profile.address}</span></a>}
                      {profile.website && <a href={profile.website.match(/^https?:\/\//) ? profile.website : `https://${profile.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:opacity-70" style={{ color: txt }}><Globe className="w-5 h-5" style={{ color: iconClr }} /><span>Visit Website</span></a>}
                    </div>
                  </div>
                )
              );

              if (sectionId === 'video') return (
                profile.video_header_url && (
                  <div key="video" className="rounded-2xl overflow-hidden mb-4" data-testid="video-section"
                    style={{ ...cardProps }}>
                    {profile.video_header_url.includes('youtube') || profile.video_header_url.includes('youtu.be') ? (
                      <iframe className="w-full aspect-video" 
                        src={`https://www.youtube.com/embed/${profile.video_header_url.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]+)/)?.[1]}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="Video" />
                    ) : (
                      <video className="w-full aspect-video object-cover" src={resolveMediaUrl(profile.video_header_url)} controls playsInline />
                    )}
                  </div>
                )
              );

              if (sectionId === 'business_hours') return (
                profile.business_hours && Object.values(profile.business_hours).some(v => v) && (
                  <div key="business_hours" className="mb-4">
                    <button
                      data-testid="hours-toggle"
                      onClick={() => setShowHours(!showHours)}
                      className="w-full transition-all hover:scale-[1.02] active:scale-[0.98] relative"
                      style={{ height: '70px' }}>
                      <div className="w-full h-full flex items-center pr-4"
                        style={{
                          paddingLeft: '60px',
                          backgroundColor: card,
                          border: `2.5px solid ${darkenColor(bgColorForCalc, 0.4)}`,
                          borderRadius: showHours ? '35px 35px 0 0' : '35px',
                          transition: 'border-radius 0.2s',
                        }}>
                        <div className="flex-1 text-center min-w-0">
                          <span className="font-semibold block text-sm" style={{ color: txt }}>Business Hours</span>
                        </div>
                        <div className="flex-shrink-0 opacity-50">
                          <MoreVertical className="w-4 h-4" style={{ color: txt }} />
                        </div>
                      </div>
                      <div className="absolute top-1/2 -translate-y-1/2 rounded-full overflow-hidden z-10 flex items-center justify-center"
                        style={{ left: '-8px', width: '60px', height: '60px', border: `2.5px solid ${darkenColor(bgColorForCalc, 0.4)}`, backgroundColor: lightenColor(bgColorForCalc, 0.1) }}>
                        <Clock className="w-6 h-6" style={{ color: iconClr }} />
                      </div>
                    </button>
                    {showHours && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="overflow-hidden"
                        style={{
                          backgroundColor: card,
                          border: `2.5px solid ${darkenColor(bgColorForCalc, 0.4)}`,
                          borderTop: 'none',
                          borderRadius: '0 0 20px 20px',
                          padding: '0 20px 16px 20px',
                        }}>
                        <div className="space-y-1.5 pt-1">
                          {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(day => profile.business_hours[day] && (
                            <div key={day} className="flex justify-between text-sm py-1 border-b last:border-0" style={{ borderColor: brd }}>
                              <span className="capitalize font-medium" style={{ color: sub }}>{day.slice(0, 3)}</span>
                              <span style={{ color: txt }}>{profile.business_hours[day]}</span>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </div>
                )
              );

              if (sectionId === 'social_grid') return (
                socialGrid && socialGrid.images?.length > 0 && (
                  <motion.div key="social_grid" data-testid="social-grid-card"
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl overflow-hidden mb-6 cursor-pointer transition-all hover:scale-[1.01]"
                    style={{ backgroundColor: card, border: `3px solid ${darkenColor(bgColorForCalc, 0.4)}` }}
                    onClick={() => {
                      if (socialGrid.profile_url) {
                        const url = socialGrid.profile_url.startsWith('http') ? socialGrid.profile_url : `https://${socialGrid.profile_url}`;
                        window.location.href = url;
                      }
                    }}>
                    <div className="p-2">
                      {socialGrid.images.length >= 5 ? (
                        <div className="rounded-xl overflow-hidden" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '3px', aspectRatio: '3 / 2' }}>
                          <img src={resolveMediaUrl(socialGrid.images[0])} alt="" className="object-cover w-full h-full" referrerPolicy="no-referrer" style={{ gridRow: '1 / 3', gridColumn: '1 / 2' }} />
                          <img src={resolveMediaUrl(socialGrid.images[1])} alt="" className="object-cover w-full h-full" referrerPolicy="no-referrer" />
                          <img src={resolveMediaUrl(socialGrid.images[2])} alt="" className="object-cover w-full h-full" referrerPolicy="no-referrer" />
                          <img src={resolveMediaUrl(socialGrid.images[3])} alt="" className="object-cover w-full h-full" referrerPolicy="no-referrer" />
                          <img src={resolveMediaUrl(socialGrid.images[4])} alt="" className="object-cover w-full h-full" referrerPolicy="no-referrer" />
                        </div>
                      ) : socialGrid.images.length >= 3 ? (
                        <div className="rounded-xl overflow-hidden" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '3px', aspectRatio: '2 / 2' }}>
                          <img src={resolveMediaUrl(socialGrid.images[0])} alt="" className="object-cover w-full h-full" referrerPolicy="no-referrer" style={{ gridRow: '1 / 3' }} />
                          {socialGrid.images.slice(1).map((img, idx) => (
                            <img key={idx} src={resolveMediaUrl(img)} alt="" className="object-cover w-full h-full" referrerPolicy="no-referrer" />
                          ))}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-1 rounded-xl overflow-hidden">
                          {socialGrid.images.map((img, idx) => (
                            <img key={idx} src={resolveMediaUrl(img)} alt="" className="w-full aspect-square object-cover" referrerPolicy="no-referrer" />
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="px-4 pb-4 pt-2 text-center relative">
                      <p className="font-semibold text-base" style={{ color: txt }}>{socialGrid.title}</p>
                      <div className="flex items-center justify-center gap-1.5 mt-1">
                        <span className="w-4 h-4 inline-flex">{getSocialIcon(socialGrid.platform, sub)}</span>
                        <span className="text-sm" style={{ color: sub }}>
                          {socialGrid.handle}{socialGrid.follower_count ? ` · ${socialGrid.follower_count}` : ''}
                        </span>
                      </div>
                      {socialGrid.profile_url && (
                        <div className="flex items-center justify-center gap-3 mt-3">
                          <span data-testid="social-grid-follow-btn"
                            className="inline-block px-6 py-2 rounded-full text-sm font-semibold transition-all hover:opacity-80"
                            style={{ backgroundColor: lightenColor(bgColorForCalc, 0.2), color: txt, border: `1.5px solid ${brd}` }}>Follow</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )
              );

              if (sectionId === 'book_appointment') {
                if (!profile.booking_enabled) return null;
                const services = profile.booking_services || [];
                const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                return (
                  <div key="book_appointment" className="mb-4" data-testid="book-appointment-section">
                    {!bookingOpen ? (
                      <button data-testid="book-appointment-btn" onClick={() => { setBookingOpen(true); setBookingStep(services.length > 0 ? 0 : 1); }}
                        className="w-full py-3.5 font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
                        style={{ ...cardProps, borderRadius: borderR, color: txt }}><CalendarDays className="w-5 h-5" />Book Appointment</button>
                    ) : (
                      <div className="rounded-2xl p-5" style={{ backgroundColor: card, border: `1.5px solid ${brd}` }}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold" style={{ color: txt }}>
                            {bookingStep === 0 && 'Select Service'}
                            {bookingStep === 1 && 'Pick a Date'}
                            {bookingStep === 2 && 'Pick a Time'}
                            {bookingStep === 3 && 'Your Details'}
                            {bookingStep === 4 && 'Confirmed!'}
                          </h3>
                          <button onClick={() => { setBookingOpen(false); setBookingStep(0); }} style={{ color: txt, opacity: 0.5 }} className="text-sm">Close</button>
                        </div>

                        {/* Step 0: Select Service */}
                        {bookingStep === 0 && (
                          <div className="space-y-2">
                            {services.map(svc => (
                              <button key={svc.id} data-testid={`select-service-${svc.id}`}
                                onClick={() => { setSelectedService(svc); setBookingStep(1); }}
                                className="w-full p-3 rounded-xl text-left transition-all hover:scale-[1.01]"
                                style={{ backgroundColor: bgColorForCalc, border: `1px solid ${brd}` }}>
                                <span className="font-medium text-sm" style={{ color: txt }}>{svc.name}</span>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs" style={{ color: txt, opacity: 0.5 }}>{svc.duration} min</span>
                                  {svc.price && <span className="text-xs font-medium" style={{ color: theme }}>{svc.price}</span>}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Step 1: Calendar */}
                        {bookingStep === 1 && (
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <button onClick={() => setBookingMonth(p => { const m = p.month - 1; return m < 0 ? { year: p.year - 1, month: 11 } : { ...p, month: m }; })}
                                style={{ color: txt }}><ChevronLeft className="w-5 h-5" /></button>
                              <span className="text-sm font-medium" style={{ color: txt }}>{monthNames[bookingMonth.month]} {bookingMonth.year}</span>
                              <button onClick={() => setBookingMonth(p => { const m = p.month + 1; return m > 11 ? { year: p.year + 1, month: 0 } : { ...p, month: m }; })}
                                style={{ color: txt }}><ChevronRight className="w-5 h-5" /></button>
                            </div>
                            <div className="grid grid-cols-7 gap-1 text-center mb-1">
                              {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(d => (
                                <span key={d} className="text-xs font-medium py-1" style={{ color: txt, opacity: 0.4 }}>{d}</span>
                              ))}
                            </div>
                            <div className="grid grid-cols-7 gap-1">
                              {getCalendarDays().map((d, i) => d ? (
                                <button key={i} data-testid={`cal-day-${d.day}`}
                                  disabled={!d.isAvailable}
                                  onClick={() => d.isAvailable && handleDateSelect(d.date)}
                                  className={`py-2 rounded-lg text-xs font-medium transition-all ${d.isAvailable ? 'hover:scale-105' : ''}`}
                                  style={{
                                    backgroundColor: selectedDate === d.date ? theme : d.isAvailable ? 'rgba(255,255,255,0.05)' : 'transparent',
                                    color: selectedDate === d.date ? (isLightColor(theme) ? '#000' : '#fff') : d.isAvailable ? txt : `${txt}30`,
                                    cursor: d.isAvailable ? 'pointer' : 'default',
                                  }}>
                                  {d.day}
                                </button>
                              ) : <div key={i} />)}
                            </div>
                            {services.length > 0 && (
                              <button onClick={() => setBookingStep(0)} className="text-xs mt-3" style={{ color: theme }}>Back to services</button>
                            )}
                          </div>
                        )}

                        {/* Step 2: Time Slots */}
                        {bookingStep === 2 && (
                          <div>
                            <p className="text-xs mb-3" style={{ color: txt, opacity: 0.5 }}>{selectedDate}</p>
                            {loadingSlots ? (
                              <p className="text-sm text-center py-4" style={{ color: txt, opacity: 0.3 }}>Loading slots...</p>
                            ) : availableSlots.length === 0 ? (
                              <p className="text-sm text-center py-4" style={{ color: txt, opacity: 0.3 }}>No available slots</p>
                            ) : (
                              <div className="grid grid-cols-3 gap-2">
                                {availableSlots.map(slot => (
                                  <button key={slot} data-testid={`slot-${slot}`}
                                    onClick={() => { setSelectedSlot(slot); setBookingStep(3); }}
                                    className="py-2 rounded-lg text-xs font-medium transition-all hover:scale-105"
                                    style={{ backgroundColor: selectedSlot === slot ? theme : 'rgba(255,255,255,0.05)', color: selectedSlot === slot ? (isLightColor(theme) ? '#000' : '#fff') : txt, border: `1px solid ${brd}` }}>
                                    {slot}
                                  </button>
                                ))}
                              </div>
                            )}
                            <button onClick={() => setBookingStep(1)} className="text-xs mt-3" style={{ color: theme }}>Back to calendar</button>
                          </div>
                        )}

                        {/* Step 3: Contact Details */}
                        {bookingStep === 3 && (
                          <form onSubmit={handleBookingSubmit} className="space-y-3">
                            <p className="text-xs mb-2" style={{ color: txt, opacity: 0.5 }}>
                              {selectedService?.name && `${selectedService.name} — `}{selectedDate} at {selectedSlot}
                            </p>
                            <Input data-testid="booking-name" value={bookingForm.name} onChange={(e) => setBookingForm(p => ({ ...p, name: e.target.value }))}
                              placeholder="Your name *" required className="rounded-full" style={{ backgroundColor: bgColorForCalc, borderColor: brd, color: txt }} />
                            <Input data-testid="booking-email" type="email" value={bookingForm.email} onChange={(e) => setBookingForm(p => ({ ...p, email: e.target.value }))}
                              placeholder="Your email *" required className="rounded-full" style={{ backgroundColor: bgColorForCalc, borderColor: brd, color: txt }} />
                            <Input data-testid="booking-phone" value={bookingForm.phone} onChange={(e) => setBookingForm(p => ({ ...p, phone: e.target.value }))}
                              placeholder="Phone (optional)" className="rounded-full" style={{ backgroundColor: bgColorForCalc, borderColor: brd, color: txt }} />
                            <Textarea data-testid="booking-message" value={bookingForm.message} onChange={(e) => setBookingForm(p => ({ ...p, message: e.target.value }))}
                              placeholder="Notes (optional)" rows={2} className="rounded-xl resize-none" style={{ backgroundColor: bgColorForCalc, borderColor: brd, color: txt }} />
                            <div className="flex gap-3">
                              <Button type="button" variant="outline" onClick={() => setBookingStep(2)} className="flex-1 rounded-full" style={{ borderColor: brd, color: txt }}>Back</Button>
                              <Button data-testid="submit-booking" type="submit" disabled={submitting} className="flex-1 rounded-full" style={{ backgroundColor: theme, color: isLightColor(theme) ? '#000' : '#fff' }}>
                                {submitting ? 'Booking...' : 'Confirm Booking'}
                              </Button>
                            </div>
                          </form>
                        )}

                        {/* Step 4: Confirmation + Add to Calendar */}
                        {bookingStep === 4 && bookingResult && (
                          <div className="text-center py-4">
                            <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: `${theme}20` }}>
                              <CalendarDays className="w-7 h-7" style={{ color: theme }} />
                            </div>
                            <p className="font-semibold mb-1" style={{ color: txt }}>Booking Confirmed!</p>
                            <p className="text-sm mb-1" style={{ color: txt, opacity: 0.6 }}>
                              {bookingResult.service_name} — {bookingResult.date} at {bookingResult.time_slot}
                            </p>
                            <p className="text-xs mb-4" style={{ color: txt, opacity: 0.4 }}>You'll receive a confirmation soon.</p>
                            <a data-testid="download-ics-visitor" href={`${API}/bookings/${bookingResult.id}/ics`} download
                              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all hover:scale-[1.02]"
                              style={{ backgroundColor: theme, color: isLightColor(theme) ? '#000' : '#fff' }}>
                              <Download className="w-4 h-4" /> Add to Calendar
                            </a>
                            <button onClick={() => { setBookingOpen(false); setBookingStep(0); setBookingResult(null); setSelectedService(null); setSelectedDate(''); setSelectedSlot(''); setBookingForm({ name: '', email: '', phone: '', message: '' }); }}
                              className="block mx-auto mt-3 text-xs" style={{ color: txt, opacity: 0.4 }}>Close</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              }

              if (sectionId === 'cta_button') return (
                <div key="cta_button" className="space-y-3 mb-4">
                  {profile.cta_button?.text && (
                    <button data-testid="cta-button" onClick={handleCTAClick} className="w-full py-3.5 font-semibold transition-all hover:opacity-90 hover:scale-[1.02] active:scale-[0.98]"
                      style={{ backgroundColor: theme, color: isLightColor(theme) ? '#000' : '#fff', borderRadius: borderR }}>{profile.cta_button.text}</button>
                  )}
                  {profile.whatsapp_number && (
                    <a data-testid="whatsapp-button" href={`https://wa.me/${profile.whatsapp_number.replace(/\D/g, '')}${profile.whatsapp_message ? `?text=${encodeURIComponent(profile.whatsapp_message)}` : ''}`} target="_blank" rel="noopener noreferrer"
                      className="w-full py-3.5 font-semibold bg-[#25D366] text-white flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
                      style={{ borderRadius: borderR }}>
                      {getSocialIcon('whatsapp', '#fff')}Chat on WhatsApp
                    </a>
                  )}
                </div>
              );

              if (sectionId === 'exchange_contact') {
                if (!profile.show_contact_form) return null;
                const enabledFields = (profile.contact_form_fields || []).filter(f => f.enabled);
                if (enabledFields.length === 0) return null;
                return (
                  <div key="exchange_contact" className="mb-4" data-testid="exchange-contact-section">
                    {!showContactForm ? (
                      <button data-testid="exchange-contact-btn" onClick={() => setShowContactForm(true)} className="w-full py-3.5 font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
                        style={{ ...cardProps, borderRadius: borderR, color: txt }}><Users className="w-5 h-5" />Exchange Contact</button>
                    ) : (
                      <div className="rounded-2xl p-5" style={{ backgroundColor: card, border: `1.5px solid ${brd}` }}>
                        <h3 className="font-semibold mb-4" style={{ color: txt }}>Exchange Contact</h3>
                        <form onSubmit={handleContactSubmit} className="space-y-3">
                          {enabledFields.map(field => (
                            <Input key={field.id} data-testid={`exchange-field-${field.id}`} type={field.type || 'text'} value={contactFormData[field.id] || ''} onChange={(e) => setContactFormData(prev => ({ ...prev, [field.id]: e.target.value }))} placeholder={field.placeholder || field.label} className="rounded-full" style={{ backgroundColor: bgColorForCalc, borderColor: brd, color: txt }} required={field.id === 'name' || field.id === 'email'} />
                          ))}
                          <div className="flex gap-3">
                            <Button type="button" variant="outline" onClick={() => setShowContactForm(false)} className="flex-1 rounded-full" style={{ borderColor: brd, color: txt }}>Cancel</Button>
                            <Button data-testid="submit-exchange-btn" type="submit" disabled={submitting} className="flex-1 rounded-full" style={{ backgroundColor: theme, color: isLightColor(theme) ? '#000' : '#fff' }}>{submitting ? 'Sending...' : 'Submit'}</Button>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                );
              }

              if (sectionId === 'save_contact') return (
                <div key="save_contact" className="mb-4">
                  <button data-testid="save-contact-btn" onClick={handleSaveContact} className="w-full py-3.5 font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
                    style={{ ...cardProps, borderRadius: borderR, color: txt }}><Download className="w-5 h-5" />Save Contact</button>
                </div>
              );

              if (sectionId === 'testimonials') return (
                profile.testimonials?.length > 0 && (
                  <div key="testimonials" className="mb-4" data-testid="testimonials-section">
                    <div className="overflow-x-auto pb-2 -mx-2 px-2 flex gap-3" style={{ scrollSnapType: 'x mandatory' }}>
                      {profile.testimonials.map((t, i) => (
                        <div key={i} className="min-w-[280px] p-4 rounded-2xl flex-shrink-0" style={{ ...cardProps, scrollSnapAlign: 'start' }}>
                          <div className="flex items-center gap-1 mb-2">
                            {Array.from({ length: 5 }).map((_, s) => (
                              <svg key={s} className="w-4 h-4" fill={s < (t.rating || 5) ? '#FBBF24' : '#374151'} viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                            ))}
                          </div>
                          <p className="text-sm italic mb-2" style={{ color: txt }}>"{t.text}"</p>
                          <p className="text-xs font-semibold" style={{ color: sub }}>— {t.author}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              );

              if (sectionId === 'appreciations') return (
                publicAppreciations.length > 0 && (
                  <div key="appreciations" className="mb-4" data-testid="appreciations-section">
                    <p className="text-sm font-semibold mb-3 px-1" style={{ color: sub }}>Appreciations</p>
                    <div className="overflow-x-auto pb-2 -mx-2 px-2 flex gap-3" style={{ scrollSnapType: 'x mandatory' }}>
                      {publicAppreciations.map((a) => (
                        <div key={a.id} className="min-w-[280px] p-4 rounded-2xl flex-shrink-0" style={{ ...cardProps, scrollSnapAlign: 'start' }}>
                          <div className="flex items-center gap-1 mb-2">
                            {Array.from({ length: 5 }).map((_, s) => (
                              <svg key={s} className="w-4 h-4" fill={s < a.rating ? '#FBBF24' : '#374151'} viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                            ))}
                          </div>
                          <p className="text-sm italic mb-2" style={{ color: txt }}>"{a.message}"</p>
                          <p className="text-xs font-semibold" style={{ color: sub }}>— @{a.from_username}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              );

              if (sectionId === 'network_stats') return (
                (refStats.given > 0 || refStats.received > 0) && (
                  <div key="network_stats" className="mb-4" data-testid="network-stats-section">
                    <div className="flex gap-3 justify-center">
                      <div className="flex-1 p-3 rounded-2xl text-center" style={cardProps}>
                        <p className="text-xl font-bold" style={{ color: theme }}>{refStats.given}</p>
                        <p className="text-xs mt-1" style={{ color: sub }}>Referrals Given</p>
                      </div>
                      <div className="flex-1 p-3 rounded-2xl text-center" style={cardProps}>
                        <p className="text-xl font-bold" style={{ color: theme }}>{refStats.received}</p>
                        <p className="text-xs mt-1" style={{ color: sub }}>Referrals Received</p>
                      </div>
                    </div>
                  </div>
                )
              );

              if (sectionId === 'review_generator') {
                const cats = profile.review_categories || [];
                const hasUrl = profile.google_review_url;
                if (cats.length === 0 || !hasUrl) return null;
                const rs = reviewState;
                const toggleCat = (cat) => {
                  setReviewState(prev => ({
                    ...prev,
                    selected: prev.selected.includes(cat) ? prev.selected.filter(c => c !== cat) : [...prev.selected, cat],
                  }));
                };
                const generateReview = async () => {
                  setReviewState(prev => ({ ...prev, loading: true }));
                  try {
                    const res = await axios.post(`${API}/generate-review`, {
                      categories: rs.selected,
                      tone: rs.tone,
                      business_name: profile.display_name || profile.business_name || profile.owner_name || '',
                    });
                    setReviewState(prev => ({ ...prev, reviews: res.data.reviews || [], chosenIdx: 0, loading: false, step: 'result' }));
                  } catch {
                    toast.error('Failed to generate review');
                    setReviewState(prev => ({ ...prev, loading: false }));
                  }
                };
                const copyAndRedirect = () => {
                  navigator.clipboard.writeText(rs.reviews[rs.chosenIdx]);
                  toast.success('Review copied! Opening Google Reviews...');
                  const link = document.createElement('a');
                  link.href = profile.google_review_url;
                  link.target = '_blank';
                  link.rel = 'noopener noreferrer';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                };
                const getReviewChipStyle = (active) => ({
                  backgroundColor: active
                    ? theme
                    : light ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)',
                  color: active
                    ? (isLightColor(theme) ? '#000' : '#fff')
                    : txt,
                  border: `1.5px solid ${
                    active ? theme : light ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.15)'
                  }`,
                });
                return (
                  <div key="review_generator" className="mb-4" data-testid="review-generator-section">
                    <div className="p-5 rounded-2xl" style={cardProps}>
                      <div className="flex items-center gap-2 mb-3">
                        <svg className="w-5 h-5" fill={theme} viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                        <span className="font-semibold text-sm" style={{ color: txt }}>Leave a Review</span>
                      </div>

                      {rs.step === 'select' && (
                        <div>
                          <p className="text-xs mb-3" style={{ color: txt, opacity: 0.8 }}>Select what you loved about us:</p>
                          <div className="flex flex-wrap gap-1.5 mb-4">
                            {cats.map(cat => (
                              <button key={cat} data-testid={`review-cat-${cat.replace(/\s/g, '-')}`} onClick={() => toggleCat(cat)}
                                className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                                style={getReviewChipStyle(rs.selected.includes(cat))}>
                                {rs.selected.includes(cat) ? '✓ ' : ''}{cat}
                              </button>
                            ))}
                          </div>

                          <div className="flex items-center gap-3 mb-4">
                            <span className="text-xs" style={{ color: txt, opacity: 0.8 }}>Tone:</span>
                            {['casual', 'professional'].map(t => (
                              <button key={t} data-testid={`tone-${t}`} onClick={() => setReviewState(prev => ({ ...prev, tone: t }))}
                                className="px-3 py-1 rounded-full text-xs font-medium transition-all"
                                style={getReviewChipStyle(rs.tone === t)}>
                                {t === 'casual' ? 'Casual' : 'Professional'}
                              </button>
                            ))}
                          </div>

                          <button data-testid="generate-review-btn" onClick={generateReview}
                            disabled={rs.selected.length === 0 || rs.loading}
                            className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all disabled:opacity-40"
                            style={{ backgroundColor: theme, color: isLightColor(theme) ? '#000' : '#fff' }}>
                            {rs.loading ? 'Generating...' : 'Generate Review with AI'}
                          </button>
                        </div>
                      )}

                      {rs.step === 'result' && rs.reviews.length > 0 && (
                        <div>
                          <p className="text-xs mb-3" style={{ color: txt, opacity: 0.8 }}>Pick the review you like best:</p>
                          <div className="space-y-2 mb-4">
                            {rs.reviews.map((rev, idx) => (
                              <button key={idx} data-testid={`review-option-${idx}`}
                                onClick={() => setReviewState(prev => ({ ...prev, chosenIdx: idx }))}
                                className="w-full text-left p-3 rounded-xl transition-all text-sm leading-relaxed"
                                style={{
                                  backgroundColor: rs.chosenIdx === idx ? `${theme}15` : light ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.03)',
                                  border: `2px solid ${rs.chosenIdx === idx ? `${theme}60` : 'transparent'}`,
                                  color: txt,
                                }}>
                                {rev}
                              </button>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <button data-testid="copy-redirect-btn" onClick={copyAndRedirect}
                              className="flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all"
                              style={{ backgroundColor: theme, color: isLightColor(theme) ? '#000' : '#fff' }}>
                              Copy & Go to Google Reviews
                            </button>
                            <button data-testid="regenerate-btn" onClick={() => setReviewState(prev => ({ ...prev, step: 'select', reviews: [] }))}
                              className="px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
                              style={{ border: `1.5px solid ${txt}30`, color: txt }}>
                              Edit
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }

              if (sectionId === 'videos') {
                const vids = profile.reels?.filter(v => v.is_active && v.type === 'upload') || [];
                if (vids.length === 0) return null;
                return (
                  <div key="videos" className="mb-4" data-testid="videos-section">
                    <p className="text-sm font-semibold mb-3 px-1" style={{ color: sub }}>Videos</p>
                    {vids.length <= 3 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {vids.map((vid, vi) => (
                          <VideoThumb key={vid.id || vi} vid={vid} vi={vi} cardProps={cardProps}
                            onClick={() => setActiveVideo(vid)} />
                        ))}
                      </div>
                    ) : (
                      <div className="flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        {vids.map((vid, vi) => (
                          <div key={vid.id || vi} className="flex-shrink-0 snap-center" style={{ width: '120px' }}>
                            <VideoThumb vid={vid} vi={vi} cardProps={cardProps}
                              onClick={() => setActiveVideo(vid)} />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Video Fullscreen Modal */}
                    {activeVideo && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
                        data-testid="video-modal" onClick={() => setActiveVideo(null)}>
                        <button className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20"
                          data-testid="close-video-modal" onClick={() => setActiveVideo(null)}>
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                          </svg>
                        </button>
                        <div className="w-full max-w-sm mx-4" style={{ maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
                          <div className="rounded-2xl overflow-hidden bg-black">
                            <video
                              src={resolveMediaUrl(activeVideo.url)}
                              className="w-full" style={{ aspectRatio: '9/16' }}
                              controls autoPlay playsInline
                            />
                            {(activeVideo.title || activeVideo.caption) && (
                              <div className="p-3 bg-zinc-900">
                                {activeVideo.title && <p className="text-white text-sm font-medium">{activeVideo.title}</p>}
                                {activeVideo.caption && <p className="text-zinc-400 text-xs mt-0.5">{activeVideo.caption}</p>}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              if (sectionId === 'posters') {
                const posterList = profile.posters || [];
                if (posterList.length === 0) return null;
                return (
                  <div key="posters" className="mb-4" data-testid="posters-section">
                    <p className="text-sm font-semibold mb-3 px-1" style={{ color: sub }}>Posters</p>
                    <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                      {posterList.map((poster, pi) => {
                        const imgSrc = resolveMediaUrl(poster.image_url);
                        return (
                          <div key={poster.id || pi}
                            className="flex-shrink-0 snap-center cursor-pointer group"
                            style={{ width: '280px' }}
                            data-testid={`poster-card-${pi}`}
                            onClick={() => setActivePoster(poster)}
                          >
                            <div className="rounded-2xl overflow-hidden transition-transform group-hover:scale-[1.01]" style={cardProps}>
                              <img src={imgSrc} alt={poster.title || `Poster ${pi + 1}`}
                                className="w-full object-cover" style={{ aspectRatio: '4/5' }} />
                            </div>
                            {poster.title && (
                              <p className="text-sm font-medium mt-2 px-0.5 truncate" style={{ color: txt }}>{poster.title}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Poster Fullscreen Modal */}
                    {activePoster && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
                        data-testid="poster-modal" onClick={() => setActivePoster(null)}>
                        <button className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20"
                          data-testid="close-poster-modal" onClick={() => setActivePoster(null)}>
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                          </svg>
                        </button>
                        <div className="max-w-lg mx-4" onClick={e => e.stopPropagation()}>
                          <img src={resolveMediaUrl(activePoster.image_url)}
                            alt={activePoster.title || 'Poster'}
                            className="w-full rounded-2xl object-contain" style={{ maxHeight: '85vh' }} />
                          {activePoster.title && (
                            <p className="text-white text-center text-sm font-medium mt-3">{activePoster.title}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return null;
            })}

            {/* Contact form moved to exchange_contact section */}
          </>
        )}

        {/* === SHOP TAB CONTENT === */}
        {activeTab === 'shop' && products.length > 0 && (
          <div className="grid grid-cols-2 gap-3 mb-6">
            {products.map((p, i) => {
              const allImages = p.images?.length > 0 ? p.images : (p.image_url ? [p.image_url] : []);
              return (
              <motion.div key={p.id} data-testid={`product-${p.id}`}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="rounded-2xl overflow-hidden text-left transition-all"
                style={{ backgroundColor: card, border: `1.5px solid ${brd}` }}>
                <div className="aspect-square overflow-hidden relative">
                  {allImages.length > 1 ? (
                    <ProductImageCarousel images={allImages} title={p.title} sub={sub} bgColorForCalc={bgColorForCalc} light={light} />
                  ) : allImages.length === 1 ? (
                    <img src={resolveMediaUrl(allImages[0])} alt={p.title} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: lightenColor(bgColorForCalc, 0.1) }}>
                      <span className="text-sm font-medium" style={{ color: sub }}>{p.title}</span>
                    </div>
                  )}
                </div>
                <div className="w-full px-3 py-2.5 text-left">
                  <p className="font-semibold text-sm truncate" style={{ color: txt }}>{p.title}</p>
                  {p.price && <p className="text-sm mt-0.5" style={{ color: sub }}>${p.price}</p>}
                  {p.description && <p className="text-xs mt-1.5 line-clamp-2" style={{ color: sub }}>{p.description}</p>}
                  {p.action_url && (
                    <a
                      className="inline-flex mt-2 text-xs font-semibold hover:opacity-80 transition-opacity"
                      style={{ color: theme }}
                      href={p.action_url.startsWith('http') ? p.action_url : `https://${p.action_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {p.action_text || (profile.catalog_type === 'services' ? 'Book Now' : 'Order Now')}
                    </a>
                  )}
                </div>
              </motion.div>
              );
            })}
          </div>
        )}

        {/* === ABOUT TAB CONTENT === */}
        {activeTab === 'about' && profile.about_sections?.length > 0 && (
          <div className="space-y-6 mb-6">
            {profile.about_sections.map((section, i) => (
              <motion.div key={section.id || i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
                {section.type === 'text' ? (
                  <div className="px-1">
                    {section.heading && (
                      <h3 className="font-bold text-xl mb-3" style={{ color: txt, fontFamily: `'${fontFamily}', sans-serif` }}>{section.heading}</h3>
                    )}
                    {section.body && (
                      <p className="leading-relaxed text-base font-medium whitespace-pre-wrap" style={{ color: txt, opacity: 0.9 }}>{section.body}</p>
                    )}
                  </div>
                ) : section.type === 'image' && section.image_url ? (
                  <div>
                    <img src={resolveMediaUrl(section.image_url)} alt={section.caption || 'About'} className="w-full rounded-2xl object-cover" style={{ maxHeight: '400px' }} />
                    {section.caption && (
                      <p className="text-sm font-semibold mt-3 text-center" style={{ color: txt, opacity: 0.85 }}>{section.caption}</p>
                    )}
                  </div>
                ) : null}
              </motion.div>
            ))}
          </div>
        )}

        {/* === TRADE TAB CONTENT === */}
        {activeTab === 'trade' && tradeServices.length > 0 && (
          <div className="space-y-4 mb-6">
            {/* Expert Bio */}
            {profile.trade_bio && (
              <div className="px-1 mb-4">
                <p className="text-sm leading-relaxed" style={{ color: sub }}>{profile.trade_bio}</p>
              </div>
            )}

            {/* Services */}
            {tradeServices.map((svc, i) => {
              const typeIcons = { audio: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z', video: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z', text: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' };
              const typeColors = { audio: '#22C55E', video: '#3B82F6', text: '#F59E0B', custom: '#8B5CF6' };
              const typeColor = typeColors[svc.type] || '#8B5CF6';
              const typePath = typeIcons[svc.type] || typeIcons.text;
              return (
                <motion.div key={svc.id} data-testid={`trade-svc-${svc.id}`}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                  className="rounded-2xl overflow-hidden"
                  style={{ ...cardProps, borderRadius: borderR }}>
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${typeColor}20` }}>
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke={typeColor} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                          <path d={typePath} />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-sm" style={{ color: txt }}>{svc.title}</h3>
                        <span className="text-xs px-2 py-0.5 rounded-full mt-1 inline-block"
                          style={{ backgroundColor: `${typeColor}15`, color: typeColor }}>
                          {svc.type === 'custom' ? svc.custom_type_name : svc.type.charAt(0).toUpperCase() + svc.type.slice(1)} Call
                        </span>
                      </div>
                    </div>
                    {svc.description && <p className="text-xs mt-2" style={{ color: sub }}>{svc.description}</p>}
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-3 text-xs" style={{ color: sub }}>
                        <span>{svc.duration_minutes} min</span>
                        <span>{svc.mode === 'both' ? 'Online/Offline' : svc.mode}</span>
                      </div>
                      <span className="text-lg font-bold" style={{ color: txt }}>${svc.price}</span>
                    </div>
                    {svc.availability_days?.length > 0 && (
                      <div className="flex gap-1 mt-2">
                        {['MO','TU','WE','TH','FR','SA','SU'].map((d, di) => {
                          const full = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'][di];
                          return (
                            <span key={d} className="text-[10px] px-1.5 py-0.5 rounded"
                              style={{ backgroundColor: svc.availability_days.includes(full) ? `${typeColor}20` : light ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)', color: svc.availability_days.includes(full) ? typeColor : `${sub}60` }}>
                              {d}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <button data-testid={`request-session-${svc.id}`}
                      onClick={() => setTradeRequestForm({ open: true, service: svc, name: '', email: '', message: '', step: 0, selectedDate: '', selectedTime: '' })}
                      className="w-full mt-3 py-2.5 rounded-xl text-sm font-semibold transition-all hover:brightness-110"
                      style={{ backgroundColor: theme, color: isLightColor(theme) ? '#000' : '#fff' }}>
                      Request a Session
                    </button>
                  </div>
                </motion.div>
              );
            })}

            {/* Portfolio */}
            {profile.trade_portfolio?.length > 0 && (
              <div className="mt-6">
                <p className="text-sm font-semibold mb-3 px-1" style={{ color: txt }}>Portfolio</p>
                <div className="space-y-3">
                  {profile.trade_portfolio.map((item, i) => (
                    <motion.div key={item.id || i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                      className="rounded-2xl overflow-hidden" style={{ ...cardProps, borderRadius: borderR }}>
                      {item.image_url && <img src={resolveMediaUrl(item.image_url)} alt={item.title} className="w-full aspect-video object-cover" />}
                      <div className="p-3">
                        {item.title && <h4 className="font-semibold text-sm" style={{ color: txt }}>{item.title}</h4>}
                        {item.description && <p className="text-xs mt-1" style={{ color: sub }}>{item.description}</p>}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Request Session Modal */}
            {tradeRequestForm.open && (() => {
              const trf = tradeRequestForm;
              const svc = trf.service;
              const availDays = svc?.availability_days || [];
              const dayMap = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
              const availDayNums = availDays.map(d => dayMap[d]);
              const mNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
              const today = new Date();

              const getCalDays = () => {
                const first = new Date(tradeMonth.year, tradeMonth.month, 1);
                const lastDay = new Date(tradeMonth.year, tradeMonth.month + 1, 0).getDate();
                let startDow = first.getDay(); if (startDow === 0) startDow = 7;
                const days = [];
                for (let i = 1; i < startDow; i++) days.push(null);
                for (let d = 1; d <= lastDay; d++) {
                  const dt = new Date(tradeMonth.year, tradeMonth.month, d);
                  const dow = dt.getDay();
                  const isAvail = availDayNums.includes(dow) && dt >= new Date(today.getFullYear(), today.getMonth(), today.getDate());
                  const dateStr = `${tradeMonth.year}-${String(tradeMonth.month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                  days.push({ day: d, date: dateStr, isAvailable: isAvail });
                }
                return days;
              };

              const timeSlots = [];
              for (let h = 9; h <= 20; h++) {
                timeSlots.push(`${h.toString().padStart(2, '0')}:00`);
                if (h < 20) timeSlots.push(`${h.toString().padStart(2, '0')}:30`);
              }

              return (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center" onClick={() => setTradeRequestForm(p => ({ ...p, open: false }))}>
                  <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                    className="w-full max-w-md rounded-t-3xl p-5 space-y-3 max-h-[80vh] overflow-y-auto"
                    style={{ backgroundColor: light ? '#ffffff' : '#1a1a1a', borderTop: `2px solid ${brd}` }}
                    onClick={(e) => e.stopPropagation()}>
                    <div className="w-10 h-1 bg-white/20 rounded-full mx-auto" />

                    <div className="flex items-center justify-between">
                      <h3 className="font-bold" style={{ color: txt }}>
                        {trf.step === 0 && 'Pick a Date'}
                        {trf.step === 1 && 'Pick a Time'}
                        {trf.step === 2 && 'Your Details'}
                      </h3>
                      <button onClick={() => setTradeRequestForm(p => ({ ...p, open: false }))} style={{ color: sub }}>Close</button>
                    </div>

                    <p className="text-xs" style={{ color: sub }}>
                      {svc?.title} — {svc?.duration_minutes}min — ${svc?.price}
                    </p>

                    {/* Step 0: Calendar */}
                    {trf.step === 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <button onClick={() => setTradeMonth(p => { const m = p.month - 1; return m < 0 ? { year: p.year - 1, month: 11 } : { ...p, month: m }; })}
                            style={{ color: txt }}><ChevronLeft className="w-5 h-5" /></button>
                          <span className="text-sm font-medium" style={{ color: txt }}>{mNames[tradeMonth.month]} {tradeMonth.year}</span>
                          <button onClick={() => setTradeMonth(p => { const m = p.month + 1; return m > 11 ? { year: p.year + 1, month: 0 } : { ...p, month: m }; })}
                            style={{ color: txt }}><ChevronRight className="w-5 h-5" /></button>
                        </div>
                        <div className="grid grid-cols-7 gap-1 text-center mb-1">
                          {['Mo','Tu','We','Th','Fr','Sa','Su'].map(d => (
                            <span key={d} className="text-xs font-medium py-1" style={{ color: txt, opacity: 0.4 }}>{d}</span>
                          ))}
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                          {getCalDays().map((d, i) => d ? (
                            <button key={i} disabled={!d.isAvailable}
                              onClick={() => { setTradeRequestForm(p => ({ ...p, selectedDate: d.date, step: 1 })); }}
                              className="py-2 rounded-lg text-xs font-medium transition-all"
                              style={{
                                backgroundColor: trf.selectedDate === d.date ? theme : d.isAvailable ? (light ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)') : 'transparent',
                                color: trf.selectedDate === d.date ? (isLightColor(theme) ? '#000' : '#fff') : d.isAvailable ? txt : `${txt}20`,
                                cursor: d.isAvailable ? 'pointer' : 'default',
                              }}>
                              {d.day}
                            </button>
                          ) : <div key={i} />)}
                        </div>
                      </div>
                    )}

                    {/* Step 1: Time Slots */}
                    {trf.step === 1 && (
                      <div>
                        <p className="text-xs mb-2" style={{ color: sub }}>{trf.selectedDate}</p>
                        <div className="grid grid-cols-3 gap-2 max-h-[40vh] overflow-y-auto">
                          {timeSlots.map(t => (
                            <button key={t}
                              onClick={() => setTradeRequestForm(p => ({ ...p, selectedTime: t, step: 2 }))}
                              className="py-2 rounded-xl text-sm font-medium transition-all"
                              style={{
                                backgroundColor: trf.selectedTime === t ? theme : light ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
                                color: trf.selectedTime === t ? (isLightColor(theme) ? '#000' : '#fff') : txt,
                                border: `1px solid ${trf.selectedTime === t ? theme : brd}`,
                              }}>
                              {t}
                            </button>
                          ))}
                        </div>
                        <button onClick={() => setTradeRequestForm(p => ({ ...p, step: 0 }))}
                          className="text-xs mt-3" style={{ color: theme }}>Back to calendar</button>
                      </div>
                    )}

                    {/* Step 2: Details */}
                    {trf.step === 2 && (
                      <div className="space-y-3">
                        <div className="p-2 rounded-lg text-xs" style={{ backgroundColor: light ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)', color: sub }}>
                          {trf.selectedDate} at {trf.selectedTime} — {svc?.duration_minutes}min
                        </div>
                        <input data-testid="trade-req-name" type="text" placeholder="Your Name"
                          value={trf.name} onChange={(e) => setTradeRequestForm(p => ({ ...p, name: e.target.value }))}
                          className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                          style={{ backgroundColor: light ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.08)', color: txt, border: `1px solid ${brd}` }} />
                        <input data-testid="trade-req-email" type="email" placeholder="Your Email"
                          value={trf.email} onChange={(e) => setTradeRequestForm(p => ({ ...p, email: e.target.value }))}
                          className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                          style={{ backgroundColor: light ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.08)', color: txt, border: `1px solid ${brd}` }} />
                        <textarea data-testid="trade-req-message" placeholder="Tell them what you need help with..."
                          value={trf.message} onChange={(e) => setTradeRequestForm(p => ({ ...p, message: e.target.value }))}
                          className="w-full px-4 py-3 rounded-xl text-sm outline-none resize-none" rows={3}
                          style={{ backgroundColor: light ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.08)', color: txt, border: `1px solid ${brd}` }} />
                        <button data-testid="submit-trade-request"
                          onClick={async () => {
                            if (!trf.name || !trf.email) { toast.error('Name and email required'); return; }
                            try {
                              await axios.post(`${API}/trade/request/${username}`, {
                                service_id: svc.id,
                                customer_name: trf.name,
                                customer_email: trf.email,
                                customer_message: `${trf.message}\n\nRequested: ${trf.selectedDate} at ${trf.selectedTime}`,
                              });
                              toast.success('Request sent!');
                              setTradeRequestForm({ open: false, service: null, name: '', email: '', message: '', step: 0, selectedDate: '', selectedTime: '' });
                            } catch { toast.error('Failed to send request'); }
                          }}
                          className="w-full py-3 rounded-xl text-sm font-bold transition-all hover:brightness-110"
                          style={{ backgroundColor: theme, color: isLightColor(theme) ? '#000' : '#fff' }}>
                          Confirm & Send Request
                        </button>
                        <button onClick={() => setTradeRequestForm(p => ({ ...p, step: 1 }))}
                          className="w-full text-xs" style={{ color: theme }}>Back to time selection</button>
                      </div>
                    )}
                  </motion.div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ══════ EVENTS TAB ══════ */}
        {activeTab === 'events' && events.length > 0 && (
          <div className="space-y-4 mb-6">
            {events.map((evt, i) => {
              const now = new Date();
              const start = new Date(`${evt.date_start}T${evt.time_start || '00:00'}`);
              const endD = evt.date_end || evt.date_start;
              const end = new Date(`${endD}T${evt.time_end || '23:59'}`);
              const status = now < start ? 'upcoming' : (now <= end ? 'live' : 'past');
              const statusColors = { upcoming: '#3B82F6', live: '#22C55E', past: '#71717A' };
              const statusLabels = { upcoming: 'Upcoming', live: 'Live Now', past: 'Past' };
              const stColor = statusColors[status];
              const isFull = evt.max_capacity && (evt.rsvp_count || 0) >= evt.max_capacity;
              const isRsvpOpen = rsvpForm.eventId === evt.id;
              const typeLabels = { in_person: 'In-Person', online: 'Online', hybrid: 'Hybrid' };

              return (
                <motion.div key={evt.id} data-testid={`event-card-${evt.id}`}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                  className="rounded-2xl overflow-hidden"
                  style={{ ...cardProps, borderRadius: borderR }}>
                  {(() => {
                    const mediaItems = [];
                    if (evt.poster_url) mediaItems.push({ type: 'image', src: evt.poster_url });
                    if (evt.video_url) {
                      const vu = evt.video_url;
                      const ytMatch = vu.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
                      const vimeoMatch = vu.match(/vimeo\.com\/(\d+)/);
                      if (ytMatch) mediaItems.push({ type: 'youtube', id: ytMatch[1] });
                      else if (vimeoMatch) mediaItems.push({ type: 'vimeo', id: vimeoMatch[1] });
                      else mediaItems.push({ type: 'video', src: vu });
                    }
                    if (mediaItems.length === 0) return null;
                    if (mediaItems.length === 1) {
                      const m = mediaItems[0];
                      if (m.type === 'image') return <img src={resolveMediaUrl(m.src)} alt={evt.title} className="w-full aspect-video object-cover" />;
                      if (m.type === 'youtube') return <iframe src={`https://www.youtube.com/embed/${m.id}`} className="w-full aspect-video" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={evt.title} />;
                      if (m.type === 'vimeo') return <iframe src={`https://player.vimeo.com/video/${m.id}`} className="w-full aspect-video" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title={evt.title} />;
                      if (m.type === 'video') return <video src={resolveMediaUrl(m.src)} controls className="w-full aspect-video object-cover" />;
                    }
                    return <EventMediaCarousel items={mediaItems} title={evt.title} theme={theme} />;
                  })()}
                  <div className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-base" style={{ color: txt }}>{evt.title}</h3>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                            style={{ backgroundColor: `${stColor}20`, color: stColor }}>
                            {statusLabels[status]}
                          </span>
                        </div>
                        {evt.organized_by && (
                          <p className="text-xs mt-0.5" style={{ color: sub }}>By {evt.organized_by}</p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        {parseFloat(evt.price) > 0 ? (
                          <span className="text-lg font-bold" style={{ color: txt }}>${evt.price}</span>
                        ) : (
                          <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#22C55E20', color: '#22C55E' }}>Free</span>
                        )}
                      </div>
                    </div>

                    {evt.description && (
                      <p className="text-sm leading-relaxed" style={{ color: sub }}>{evt.description}</p>
                    )}

                    <div className="space-y-1.5">
                      {evt.date_start && (
                        <div className="flex items-center gap-2 text-xs" style={{ color: sub }}>
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                          {new Date(evt.date_start + 'T00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })}
                          {evt.time_start && ` at ${evt.time_start}`}
                          {evt.time_end && ` - ${evt.time_end}`}
                        </div>
                      )}
                      {evt.location_name && (
                        <div className="flex items-center gap-2 text-xs" style={{ color: sub }}>
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                          {evt.location_name}{evt.location_address && ` — ${evt.location_address}`}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs" style={{ color: sub }}>
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
                        {evt.rsvp_count || 0} going{evt.max_capacity ? ` / ${evt.max_capacity} spots` : ''}
                        <span className="px-1.5 py-0.5 rounded text-[10px]" style={{ backgroundColor: `${sub}15`, color: sub }}>
                          {typeLabels[evt.event_type] || 'In-Person'}
                        </span>
                      </div>
                      {evt.meeting_link && (evt.event_type === 'online' || evt.event_type === 'hybrid') && (
                        <a href={evt.meeting_link} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 text-xs font-medium"
                          style={{ color: theme }}>
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15,3 21,3 21,9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                          Join Meeting
                        </a>
                      )}
                    </div>

                    {/* RSVP Button / Form */}
                    {status !== 'past' && !isFull && (
                      <>
                        {!isRsvpOpen ? (
                          <button data-testid={`rsvp-btn-${evt.id}`}
                            onClick={() => setRsvpForm({ eventId: evt.id, name: '', email: '', phone: '' })}
                            className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all hover:brightness-110"
                            style={{ backgroundColor: theme, color: isLightColor(theme) ? '#000' : '#fff' }}>
                            RSVP Now
                          </button>
                        ) : (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                            className="space-y-2 pt-2">
                            <input data-testid={`rsvp-name-${evt.id}`} placeholder="Your name *"
                              value={rsvpForm.name} onChange={e => setRsvpForm(f => ({ ...f, name: e.target.value }))}
                              className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
                              style={{ backgroundColor: light ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.08)', color: txt, border: `1px solid ${brd}` }} />
                            <input data-testid={`rsvp-email-${evt.id}`} placeholder="Your email *" type="email"
                              value={rsvpForm.email} onChange={e => setRsvpForm(f => ({ ...f, email: e.target.value }))}
                              className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
                              style={{ backgroundColor: light ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.08)', color: txt, border: `1px solid ${brd}` }} />
                            <input data-testid={`rsvp-phone-${evt.id}`} placeholder="Phone (optional)"
                              value={rsvpForm.phone} onChange={e => setRsvpForm(f => ({ ...f, phone: e.target.value }))}
                              className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
                              style={{ backgroundColor: light ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.08)', color: txt, border: `1px solid ${brd}` }} />
                            <div className="flex gap-2">
                              <button
                                onClick={() => setRsvpForm({ eventId: null, name: '', email: '', phone: '' })}
                                className="flex-1 py-2.5 rounded-xl text-sm font-medium"
                                style={{ color: sub }}>
                                Cancel
                              </button>
                              <button data-testid={`rsvp-submit-${evt.id}`}
                                disabled={rsvpSubmitting}
                                onClick={async () => {
                                  if (!rsvpForm.name || !rsvpForm.email) { toast.error('Name and email required'); return; }
                                  setRsvpSubmitting(true);
                                  try {
                                    const res = await axios.post(`${API}/events/${evt.id}/rsvp`, {
                                      name: rsvpForm.name, email: rsvpForm.email, phone: rsvpForm.phone
                                    });
                                    toast.success("You're in! RSVP confirmed.");
                                    setEvents(prev => prev.map(e => e.id === evt.id ? { ...e, rsvp_count: res.data.rsvp_count } : e));
                                    setRsvpForm({ eventId: null, name: '', email: '', phone: '' });
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Failed to RSVP');
                                  } finally { setRsvpSubmitting(false); }
                                }}
                                className="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all hover:brightness-110"
                                style={{ backgroundColor: theme, color: isLightColor(theme) ? '#000' : '#fff' }}>
                                {rsvpSubmitting ? 'Confirming...' : 'Confirm RSVP'}
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </>
                    )}
                    {isFull && status !== 'past' && (
                      <div className="text-center py-2 text-xs font-medium" style={{ color: '#EF4444' }}>Event is at full capacity</div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Footer - Join CTA */}
        <div className="text-center pt-8 pb-10 space-y-4">
          <a href="/" data-testid="join-onelink-btn"
            className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-semibold transition-all hover:scale-105 active:scale-95"
            style={{ backgroundColor: theme, color: isLightColor(theme) ? '#000' : '#fff' }}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
            Join {profile.username} on One Link
          </a>
          <p className="text-xs" style={{ color: sub, opacity: 0.5 }}>createonelink.com/{profile.username}</p>
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;
